"use client"

import {
  ChevronDown,
  ChevronUp,
  LogOut,
  Moon,
  Settings,
  Sun,
  TrendingUp
} from "lucide-react"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

import { TrackedButton, TrackedLink } from "@/components/analytics"
import { useAuth } from "@/components/AuthProvider"
import SignupLink from "@/components/SignupLink"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useCurrentScore } from "@/hooks/useGamification"
import { useUserProfile } from "@/hooks/useUserProfile"

import Avatar from "../Avatar"

export default function UserProfile() {
  const { signOut, user } = useAuth()
  const { data: profile } = useUserProfile()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [theme, setTheme] = useState<"dark" | "light">("dark")
  const { data: currentScore } = useCurrentScore() ?? {}
  const router = useRouter()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentProgress = Math.round(
    (((currentScore?.score ?? 0) - (currentScore?.level_min_score ?? 0)) /
      ((currentScore?.level_max_score ?? 1) -
        (currentScore?.level_min_score ?? 0))) *
      100
  )

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "dark"
    setTheme(savedTheme as "dark" | "light")
  }, [])

  // Close dropdown when clicking outside
  useClickOutside(
    dropdownRef,
    useCallback(() => setIsOpen(false), []),
    isOpen
  )

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark"
    setTheme(newTheme)
    localStorage.setItem("theme", newTheme)

    if (newTheme === "light") {
      document.documentElement.classList.remove("dark")
    } else {
      document.documentElement.classList.add("dark")
    }
  }

  // Helper function to get user's display name
  const getDisplayName = () => {
    if (!user) return "User"

    // First check if user has set a custom display name in their profile
    if (profile?.full_name) return profile.full_name

    // For Google OAuth users, try different name fields
    if (user.app_metadata?.provider === "google") {
      // Google provides full_name, name, or given_name + family_name
      const fullName = user.user_metadata?.full_name
      if (fullName) return fullName

      const name = user.user_metadata?.name
      if (name) return name

      // Try combining given_name and family_name
      const givenName = user.user_metadata?.given_name
      const familyName = user.user_metadata?.family_name
      if (givenName && familyName) return `${givenName} ${familyName}`
      if (givenName) return givenName
      if (familyName) return familyName
    }

    // For magic link users, try to get name from user metadata
    const fullName = user.user_metadata?.full_name
    if (fullName) return fullName

    // Fallback to email prefix
    return user.email?.split("@")[0] || "User"
  }

  const handleSignOut = async () => {
    setLoading(true)
    try {
      // Clear all AI chat histories as security measure
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith("ai-chat-storage-"))
        .forEach((key) => sessionStorage.removeItem(key))

      await signOut()
      router.push("/")
    } catch (error) {
      console.error("Error signing out:", error)
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="flex items-center space-x-4">
        <SignupLink className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
          Sign In
        </SignupLink>
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <TrackedButton
        action="Menu Toggled"
        category="nav"
        className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-blue-400 rounded-lg cursor-pointer"
        label="Profile Menu"
        onClick={() => {
          const nextOpen = !isOpen
          setIsOpen(nextOpen)
        }}
        value={!isOpen ? "Open" : "Close"}
      >
        <svg
          className="absolute -top-1 -left-1 w-8 h-8"
          height={96}
          viewBox="0 0 96 96"
          width={96}
        >
          <circle
            className="stroke-slate-300 dark:stroke-slate-400"
            cx={48}
            cy={48}
            fill="none"
            r={44}
            stroke="#cad5e2"
            strokeWidth={4}
          />
          <circle
            className="stroke-yellow-600 dark:stroke-yellow-400"
            cx={48}
            cy={48}
            fill="none"
            r={44}
            stroke="url(#avatar-gradient)"
            strokeDasharray={2 * Math.PI * 44}
            strokeDashoffset={2 * Math.PI * 44 * (1 - currentProgress / 100)}
            strokeLinecap="round"
            strokeWidth={4}
            style={{
              transition: "stroke-dashoffset 0.6s cubic-bezier(.4,1,.3,1)"
            }}
            transform="rotate(-90 48 48)"
          />
          <defs>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id="avatar-gradient"
              x1="0"
              x2="96"
              y1="0"
              y2="96"
            >
              <stop stopColor="#60a5fa" /> {/* blue-400 */}
              <stop offset="1" stopColor="#a78bfa" /> {/* purple-500 */}
            </linearGradient>
          </defs>
        </svg>
        <Avatar className="w-6 h-6" />
        <span className="hidden 2xl:block font-medium">{getDisplayName()}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </TrackedButton>

      {isOpen && (
        <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700 w-64">
          <div className="px-4 py-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white text-ellipsis overflow-hidden">
              {user.email}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-ellipsis overflow-hidden">
              {getDisplayName()}
            </p>
          </div>
          <TrackedButton
            action="Theme Changed"
            category="nav"
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            label="Theme Toggle"
            onClick={toggleTheme}
            value={theme === "dark" ? "Dark to Light" : "Light to Dark"}
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
            <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </TrackedButton>
          <hr className="border-t-0 border-b border-gray-200 dark:border-gray-700" />
          <TrackedLink
            action="Navigation Clicked"
            category="nav"
            className="text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            href="/profile"
            label="Your Profile"
          >
            <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span>Your Profile</span>
          </TrackedLink>
          <TrackedLink
            action="Navigation Clicked"
            category="nav"
            className="text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            href="/progress"
            label="Your Progress"
          >
            <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Your Progress</span>
          </TrackedLink>
          <TrackedButton
            action="User Signed Out"
            category="nav"
            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
            disabled={loading}
            label="Sign out"
            onClick={() => void handleSignOut()}
            value="Profile Menu"
          >
            <LogOut className="w-4 h-4" />
            <span>{loading ? "Signing out..." : "Sign out"}</span>
          </TrackedButton>
        </div>
      )}
    </div>
  )
}
